import { Button, LinkButton } from '@/atoms/Button'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { AsDiv, AsH4, HeadingSM, LabelMD, ParagraphSM, TitleXS } from '@/atoms/Text'
import { paths } from '@/constants'
import { CatalogTitle, findTitleProjectSlug, formatTitleMoviePosterPath } from '@/services/ContentCatalog'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { isValidDate } from '@/utils/date-utils'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { TheatricalLandingReleaseOld } from './queries'
import { TitleRelease } from './types'

export function UpcomingReleaseCard(props: {
  item: TitleRelease
  onShowTrailer: (args: { titleRelease: TitleRelease }) => void
}) {
  const { title, release } = props.item
  const { t } = useTranslate('tickets')
  const { locale } = useLocale()
  const path = formatTitleMoviePosterPath(title)
  const posterUrl = path && getCloudinaryImageUrl({ path, width: 385 })

  if (!title || !release) return null

  const hasTrailer = !!props.item?.release?.filmDetails?.trailerSourceUrl
  const learnMoreUrl = formatLearnMoreUrl(title)

  return (
    <li className="block w-[75vw] shrink-0 md:w-[420px] 2lg:w-[1160px]">
      <div
        className="h-full overflow-hidden rounded-3xl bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: posterUrl ? `url(${posterUrl})` : undefined,
        }}
      >
        <div className="relative inset-0 h-full items-center gap-[64px] p-8 backdrop-blur-3xl md:px-[52px] md:py-[62px] 2lg:flex 2lg:p-[80px]">
          <img
            className="mb-6 w-[126px] rounded-xl shadow-light-5 md:w-[158px] 2lg:mb-auto 2lg:w-[366px]"
            src={posterUrl}
            alt={title.title}
          />
          <div>
            <HeadingSM
              as={AsH4}
              weight="bold"
              color="white"
              className="md:photon-heading-xl lg:photon-display mb-2 leading-[1.12em] md:text-[48px] 2lg:mb-4 2lg:text-[60px]"
            >
              {title.title}
            </HeadingSM>
            <TitleXS
              as={AsDiv}
              color="white"
              weight="semibold"
              className="md:photon-title-md lg:photon-heading-xl mb-4 md:text-[18px] 2lg:text-[28px]"
            >
              {formatInTheatersLine(t, locale, release)}
            </TitleXS>
            <ParagraphSM
              color="white"
              className="lg:photon-paragraph-md mb-8 md:text-[18px] lg:text-[16px]"
              weight="normal"
            >
              {title.description.short}
            </ParagraphSM>
            <div className="flex flex-col gap-4 2lg:flex-row 2lg:gap-8">
              {hasTrailer && (
                <Button
                  onClick={() => {
                    props.onShowTrailer({ titleRelease: props.item })
                  }}
                  variant="white"
                  className="flex h-12 w-full items-center gap-[6px] whitespace-nowrap lg:w-fit 2lg:h-[56px]"
                >
                  <PlayIcon color="black" size={18} />
                  <LabelMD color="black" weight="semibold" className="2lg:text-[18px]">
                    {t('watchTrailer', 'Watch Trailer')}
                  </LabelMD>
                </Button>
              )}

              {learnMoreUrl ? (
                <LinkButton
                  href={learnMoreUrl}
                  variant="core-gray-800"
                  className="flex h-12 w-full items-center gap-[6px] whitespace-nowrap lg:w-fit 2lg:h-[56px]"
                >
                  <LabelMD color="white" weight="semibold" className="text-white 2lg:text-[18px]">
                    {t('learnMore', 'Learn More')}
                  </LabelMD>
                </LinkButton>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </li>
  )
}

export function formatInTheatersLine(
  t: TranslateFunction,
  locale: string,
  release: TheatricalLandingReleaseOld,
): string | undefined {
  if (!release) return

  if (release.dateDisplayOption === 'full-date') {
    const releaseDate = release?.filmDetails?.theatricalReleaseDate
    if (!releaseDate) return

    const date = new Date(releaseDate)
    if (!isValidDate(date)) return

    const formatted = date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    })
    return `${t('inTheaters', 'In Theaters')} ${formatted}`
    // NOTE: month-and-year included only for ease of migration. Current design/titles don't use it
  } else if (release.dateDisplayOption === 'year' || release.dateDisplayOption === 'month-and-year') {
    const year = release.filmDetails?.releaseYear
    return year ? `${t('comingIn', 'Coming in')} ${year}` : t('comingSoon', 'Coming Soon')
  }
}

function formatLearnMoreUrl(title: CatalogTitle): string | undefined {
  const slug = findTitleProjectSlug(title)
  return slug ? `${paths.movies.index}/${slug}` : undefined
}
